import { useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { useAppStore } from "../../shared/store/app";
import type { BarcodeLookupResponse } from "../../shared/api/requests";
import { requests } from "../../shared/api";
import { log } from "../../logger";
import { shortUuid, normalizeItemCategory, type Item } from "@egalregal/shared";
import type { CodeDecoder, ScanFormat } from "./scan";
import {
  createResolveDeps,
  manualFallbackOf,
  outcomeForResolution,
  resolveScan,
  type ResolveDeps,
} from "./resolution";
import { useScanHistoryEntry } from "./useScanHistoryEntry";
import { StagedPhotoStrip } from "../capture/StagedPhotoStrip";
import type { StagedCapture } from "../capture/CaptureToolbar";
import { useT } from "../../shared/i18n";
import { CategoryAutocomplete } from "../../shared/components/CategoryAutocomplete";
import {
  DatePropertyFields,
  datePropertiesOf,
  type DatePropertyValues,
} from "../../shared/components/DatePropertyFields";
import { useQrScanner } from "./useQrScanner";
import { ScanChip } from "./ScanChip";
import { ScanFallback } from "./ScanFallback";

export interface ItemCreatePresetProps {
  onCreated: (item: Item) => void;
  onClose: () => void;
  /** Barcode to start with in the manual phase, skipping scanning/lookup. */
  initialBarcode?: string;
  decoder?: CodeDecoder;
}

type Phase = "barcode" | "manual";

/**
 * `item-create` preset (scanner-surface 02): the items-list create dialog.
 *
 * One-shot per code: a decode (or typed barcode) runs the provider lookup once
 * and moves to the unplaced-item form. The camera stays mounted and is hidden
 * with CSS while the form is open, so scanning never restarts the stream. A
 * caller that already knows the barcode (`initialBarcode`, a scanned QR label)
 * opens straight in the manual phase with no camera and no provider lookup.
 */
export function ItemCreatePreset({
  onCreated,
  onClose,
  initialBarcode,
  decoder,
}: ItemCreatePresetProps) {
  const t = useT();
  const prefilled = initialBarcode?.trim() ?? "";
  const [barcode, setBarcode] = useState(prefilled);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("other");
  const [lookup, setLookup] = useState<BarcodeLookupResponse | null>(null);
  const [phase, setPhase] = useState<Phase>(prefilled ? "manual" : "barcode");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [staged, setStaged] = useState<StagedCapture[]>([]);
  const [dates, setDates] = useState<DatePropertyValues>({});
  const handled = useRef<string | null>(null);
  // The scan-history entry of the barcode in the form (results-04).
  const history = useScanHistoryEntry();
  // Products resolve cache-first through the provider chain, but this preset
  // never resolves a label: a plain-UUID QR is a new item's barcode, not a
  // catalog target (qr-item-tags 02). The override keeps it off the network.
  const resolverDeps = useMemo<ResolveDeps>(
    () => ({ ...createResolveDeps(), resolveLabel: async () => "not-found" }),
    [],
  );
  const { result, format, cameraError, videoRef, scanning } = useQrScanner(decoder, prefilled === "", "both");

  // Every open starts from the caller's barcode: a prefilled label lands in
  // the manual phase, a plain open starts scanning.
  const resetFields = () => {
    setBarcode(prefilled);
    setName("");
    setCategory("other");
    setLookup(null);
    setPhase(prefilled ? "manual" : "barcode");
    setError(null);
    setBusy(false);
    setStaged([]);
    setDates({});
    // Keep the handled value: the camera stays mounted while the dialog
    // closes, so clearing it would re-process the held code after `reset()`.
  };

  useEffect(() => {
    resetFields();
  }, [prefilled]);

  const reset = () => {
    resetFields();
    onClose();
  };

  const handleLookup = async (codeArg?: string, scanFormat: ScanFormat = "manual") => {
    const raw = (codeArg ?? barcode).trim();
    if (!raw) return;
    // Every decode and typed barcode lands in the per-user history (results-04).
    const entry = history.record(raw, scanFormat);
    // The camera path hands a decoded code straight in — persist it so the
    // confirm step saves the scanned barcode, not an empty one. A label keeps
    // its canonical UUID; the API stores it as barcodeType "unknown".
    const { payload } = entry;
    const code = entry.code;
    if (!code) return;
    setBarcode(code);
    setBusy(true);
    setError(null);
    try {
      const result = await resolveScan(payload, resolverDeps);
      entry.outcome(outcomeForResolution(result));
      const fallback = manualFallbackOf(result);
      // An unreachable provider keeps the old "try again" error in the barcode
      // phase; a miss, a label that is not a product and a non-barcode payload
      // all fall into the manual form.
      if (fallback === "offline") {
        log.warn({ barcode: code }, "Provider lookup offline");
        setError(t("scanToAddLookupFailed"));
        return;
      }
      if (fallback !== null) {
        setLookup(null);
        setName("");
        setCategory("other");
        setPhase("manual");
        return;
      }
      if (result.kind === "item") {
        // Already in the collection (the mirror answered first): prefill the
        // form; a confirmed create returns that same item server-side.
        setLookup(null);
        setName(result.item.name);
        setCategory(normalizeItemCategory(result.item.category));
        setPhase("manual");
        return;
      }
      if (result.kind === "product" && result.preview) {
        setLookup(result.preview);
        setName(result.preview.name);
        setCategory(normalizeItemCategory(result.preview.category));
        setPhase("manual");
        return;
      }
      // Only a product payload reaches this preset's resolver; everything else
      // is a fallback above. Never leave the barcode phase spinning.
      setPhase("manual");
    } catch (err) {
      // An unexpected answer is not a domain result (the resolver rethrows it):
      // the preset reports the failure with its own error, like before.
      log.warn({ err }, `Lookup failed for barcode ${raw}`);
      setError(t("scanToAddLookupFailed"));
    } finally {
      setBusy(false);
    }
  };

  // One-shot per code: the panel used to unmount after a decode. The camera is
  // now kept mounted, so ignore the held code and any decode once the form is
  // open; only a new value in the barcode phase starts a lookup.
  useEffect(() => {
    if (!result || handled.current === result) return;
    handled.current = result;
    if (phase === "barcode") void handleLookup(result, format ?? "unknown");
  }, [result, phase]);

  const handleSave = async () => {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const item = await requests.items.create({
        barcode: barcode.trim(),
        name: name.trim(),
        category,
        properties: datePropertiesOf(dates),
      });
      for (const capture of staged) {
        try {
          await requests.items.addPhoto(item.id, capture.file, "general", capture.location);
        } catch (err) {
          // The item was still created — surface the partial failure below.
          log.warn({ err, itemId: item.id }, "Failed to upload item photo");
          setError(t("itemAddPhotoFailed"));
        }
      }
      reset();
      // New own item should be findable immediately (mirror refresh, tkt 07/08).
      void useAppStore.getState().refreshItemMirror();
      history.outcome({
        kind: "created-item",
        id: item.id,
        label: item.name,
      });
      onCreated(item);
    } catch (err) {
      log.warn({ err, barcode: barcode.trim() }, "Failed to create item");
      setError(t("scanToAddFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {prefilled
          ? t("createItemFromLabelTitle", { id: shortUuid(prefilled) })
          : t("createItemTitle")}
      </DialogTitle>
      <DialogContent>
        {!prefilled && (
          <Box sx={{ mt: 1 }}>
            <ScanChip
              videoRef={videoRef}
              scanning={scanning}
              cameraError={cameraError}
              ariaLabel={t("scanToAddTitle")}
              cameraErrorText={t("scanCameraErrorBarcode")}
              hidden={phase !== "barcode"}
            />
          </Box>
        )}
        {phase === "barcode" && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            <ScanFallback
              value={barcode}
              onChange={setBarcode}
              onSubmit={() => void handleLookup(undefined, "manual")}
              placeholder={t("scanToAddBarcodePlaceholder")}
              submitLabel={t("scanToAddLookup")}
            />
            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Stack>
        )}
        {phase === "manual" && (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!lookup && !prefilled && (
              <Typography variant="body2" color="text.secondary">
                {t("scanToAddUnknown", { barcode })}
              </Typography>
            )}
            <TextField
              autoFocus
              fullWidth
              size="small"
              label={t("scanToAddNameLabel")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <CategoryAutocomplete
              label={t("filterType")}
              value={category}
              onChange={setCategory}
            />
            <Typography variant="overline" color="text.secondary">
              {t("itemDates")}
            </Typography>
            <DatePropertyFields values={dates} onChange={setDates} />
            <StagedPhotoStrip captures={staged} onChange={setStaged} />
            {error && (
              <Typography variant="body2" color="error">{error}</Typography>
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={reset} disabled={busy}>{t("cancel")}</Button>
        {phase === "manual" && (
          <Button variant="contained" disabled={!name.trim() || !category.trim() || busy} onClick={() => void handleSave()}>
            {t("createItemConfirm")}
          </Button>
        )}
      </DialogActions>
    </>
  );
}
